import React, { useState } from "react";
import AxisNumberInput from "../ui/AxisNumberInput";
import DragNumberInput from "../ui/DragNumberInput";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import FieldLabel from "../ui/FieldLabel";
import Separator from "../ui/Separator";
import Checkbox from "../ui/Checkbox";
import InfoTooltip from "../ui/InfoTooltip";
import TransformDisplayModeSwitches from "./TransformDisplayModeSwitches";
import { useTransformDisplayPreferences } from "./useTransformDisplayPreferences";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";

const formatTransformInputValue = (value, fallback = "0", maxFractionDigits = 9) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  if (Object.is(numericValue, -0)) return "0";
  return numericValue.toFixed(maxFractionDigits).replace(/\.?0+$/, "");
};

const OffsetEditor = ({
  globalPosition = [0, 0, 0],
  localPosition = [0, 0, 0],
  localRotationDeg = [0, 0, 0],
  quaternion = [1, 0, 0, 0],
  onGlobalPositionChange = null,
  onPositionChange = () => {},
  onRotationChange = () => {},
  onQuaternionChange = null,
  title = "Transform",
  open: openProp,
  onOpenChange,
  showAxes,
  onToggleAxes,
  disabled = false,
  disabledTitle = "Stop physics to edit transforms",
  onDragStart,
  onDragEnd,
  embedded = false,
  headerActions = null,
  children = null,
  physicsAuthoringLocked = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(true);
  const [editingInputs, setEditingInputs] = useState({});
  const { positionMode, rotationMode, setPositionMode, setRotationMode } =
    useTransformDisplayPreferences();

  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const handleOpenChange = onOpenChange || (() => setInternalOpen((v) => !v));

  const canEditGlobal = !disabled && typeof onGlobalPositionChange === "function";
  const canEditTransform = !disabled;

  /** Dragging axis labels must drive the scene every frame (throttled to rAF inside DragNumberInput). */
  const liveDragAxisProps = { deferDragUpdates: false };

  const editorContent = (
    <div className="space-y-3">
      {positionMode === "global" ? (
        <div className="property-axis-grid-container">
          <div className="mb-2 flex items-center justify-between gap-2">
            <FieldLabel>Global Position</FieldLabel>
            <TransformDisplayModeSwitches
              value={positionMode}
              leftLabel="Local"
              leftValue="local"
              rightLabel="Global"
              rightValue="global"
              onChange={setPositionMode}
            />
          </div>
          <div className="property-axis-grid grid gap-2">
            {["X", "Y", "Z"].map((axis, index) => {
              const inputKey = `global-${index}`;
              const isEditing = editingInputs[inputKey];
              const displayValue =
                isEditing !== undefined
                  ? editingInputs[inputKey]
                  : formatTransformInputValue(globalPosition[index]);
              return (
                <div key={axis} className="space-y-1">
                  <AxisNumberInput
                    axis={axis}
                    value={displayValue}
                    dragValue={globalPosition[index]}
                    {...liveDragAxisProps}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragValueChange={(nextValue) => {
                      if (!canEditGlobal) return;
                      onGlobalPositionChange(index, nextValue);
                    }}
                    onChange={(e) => {
                      if (!canEditGlobal) return;
                      const val = e.target.value;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                      const numVal = parseFloat(val);
                      if (!isNaN(numVal)) onGlobalPositionChange(index, numVal);
                    }}
                    onFocus={(e) => {
                      if (!canEditGlobal) return;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
                    }}
                    onBlur={(e) => {
                      if (!canEditGlobal) return;
                      const numVal = parseFloat(e.target.value) || 0;
                      onGlobalPositionChange(index, numVal);
                      setEditingInputs((prev) => {
                        const next = { ...prev };
                        delete next[inputKey];
                        return next;
                      });
                    }}
                    disabled={!canEditGlobal}
                    tooltipContent={disabled ? disabledTitle : null}
                    inputProps={{ step: "0.01" }}
                    labelClassName="text-xs text-gray-300 w-3 text-center"
                    inputClassName="w-full px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="property-axis-grid-container">
          <div className="mb-2 flex items-center justify-between gap-2">
            <FieldLabel>Local Position</FieldLabel>
            <TransformDisplayModeSwitches
              value={positionMode}
              leftLabel="Local"
              leftValue="local"
              rightLabel="Global"
              rightValue="global"
              onChange={setPositionMode}
            />
          </div>
          <div className="property-axis-grid grid gap-2">
            {["X", "Y", "Z"].map((axis, index) => {
              const inputKey = `pos-${index}`;
              const isEditing = editingInputs[inputKey];
              const displayValue =
                isEditing !== undefined
                  ? editingInputs[inputKey]
                  : formatTransformInputValue(localPosition[index]);
              return (
                <div key={axis} className="space-y-1">
                  <AxisNumberInput
                    axis={axis}
                    value={displayValue}
                    dragValue={localPosition[index]}
                    {...liveDragAxisProps}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragValueChange={(nextValue) => {
                      if (!canEditTransform) return;
                      onPositionChange(index, nextValue);
                    }}
                    onChange={(e) => {
                      if (!canEditTransform) return;
                      const val = e.target.value;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                      const numVal = parseFloat(val);
                      if (!isNaN(numVal)) onPositionChange(index, numVal);
                    }}
                    onFocus={(e) => {
                      if (!canEditTransform) return;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
                    }}
                    onBlur={(e) => {
                      if (!canEditTransform) return;
                      const numVal = parseFloat(e.target.value) || 0;
                      onPositionChange(index, numVal);
                      setEditingInputs((prev) => {
                        const next = { ...prev };
                        delete next[inputKey];
                        return next;
                      });
                    }}
                    disabled={!canEditTransform}
                    labelClassName="text-xs text-gray-300 w-3 text-center"
                    inputClassName="w-full px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                    tooltipContent={disabled ? disabledTitle : null}
                    inputProps={{ step: "0.01" }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Separator />

      {rotationMode === "degrees" ? (
        <div className="property-axis-grid-container">
          <div className="mb-2 flex items-center justify-between gap-2">
            <FieldLabel>Rotation (degrees)</FieldLabel>
            <TransformDisplayModeSwitches
              value={rotationMode}
              leftLabel="Deg"
              leftValue="degrees"
              rightLabel="Quat"
              rightValue="quaternion"
              onChange={setRotationMode}
            />
          </div>
          <div className="property-axis-grid grid gap-2">
            {["X", "Y", "Z"].map((axis, index) => {
              const inputKey = `rot-${index}`;
              const isEditing = editingInputs[inputKey];
              const displayValue =
                isEditing !== undefined
                  ? editingInputs[inputKey]
                  : formatTransformInputValue(localRotationDeg[index]);
              return (
                <div key={axis} className="space-y-1">
                  <AxisNumberInput
                    axis={axis}
                    value={displayValue}
                    dragValue={localRotationDeg[index]}
                    {...liveDragAxisProps}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragValueChange={(nextValue) => {
                      if (!canEditTransform) return;
                      onRotationChange(index, nextValue);
                    }}
                    onChange={(e) => {
                      if (!canEditTransform) return;
                      const val = e.target.value;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                      const numVal = parseFloat(val);
                      if (!isNaN(numVal)) onRotationChange(index, numVal);
                    }}
                    onFocus={(e) => {
                      if (!canEditTransform) return;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
                    }}
                    onBlur={(e) => {
                      if (!canEditTransform) return;
                      const numVal = parseFloat(e.target.value) || 0;
                      onRotationChange(index, numVal);
                      setEditingInputs((prev) => {
                        const next = { ...prev };
                        delete next[inputKey];
                        return next;
                      });
                    }}
                    disabled={!canEditTransform}
                    labelClassName="text-xs text-gray-300 w-3 text-center"
                    inputClassName="w-full px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                    tooltipContent={disabled ? disabledTitle : null}
                    inputProps={{ step: "0.1" }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="property-quaternion-grid-container">
          <div className="mb-2 flex items-center justify-between gap-2">
            <FieldLabel className="inline-flex items-center gap-1.5">
              Rotation (Quaternion)
              <InfoTooltip
                text="Quaternion components in [w, x, y, z] order. Use w=1 and x=y=z=0 for no rotation."
                widthClass="w-64"
              />
            </FieldLabel>
            <TransformDisplayModeSwitches
              value={rotationMode}
              leftLabel="Deg"
              leftValue="degrees"
              rightLabel="Quat"
              rightValue="quaternion"
              onChange={setRotationMode}
            />
          </div>
          <div className="property-quaternion-grid grid gap-2">
            {["w", "x", "y", "z"].map((k, idx) => {
              const value = quaternion[idx];
              const inputKey = `quat-${idx}`;
              const isEditing = editingInputs[inputKey];
              const baseValue = Number.isFinite(value) ? value : idx === 0 ? 1 : 0;
              const displayValue =
                isEditing !== undefined
                  ? editingInputs[inputKey]
                  : formatTransformInputValue(baseValue, idx === 0 ? "1" : "0", 12);
              const canEdit = !disabled && typeof onQuaternionChange === "function";
              return (
                <div key={k} className="space-y-1">
                  <DragNumberInput
                    label={k}
                    value={displayValue}
                    dragValue={baseValue}
                    {...liveDragAxisProps}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragValueChange={(nextValue) => {
                      if (!canEdit) return;
                      onQuaternionChange(idx, nextValue);
                    }}
                    onChange={(e) => {
                      if (!canEdit) return;
                      const val = e.target.value;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                      const numVal = parseFloat(val);
                      if (!isNaN(numVal)) onQuaternionChange(idx, numVal);
                    }}
                    onFocus={(e) => {
                      if (!canEdit) return;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
                    }}
                    onBlur={(e) => {
                      if (!canEdit) return;
                      const numVal = parseFloat(e.target.value);
                      if (!isNaN(numVal)) onQuaternionChange(idx, numVal);
                      setEditingInputs((prev) => {
                        const next = { ...prev };
                        delete next[inputKey];
                        return next;
                      });
                    }}
                    wrapperClassName="flex items-center gap-1"
                    labelClassName="text-xs panel-text-helper uppercase w-3 text-center"
                    inputClassName="w-full px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                    tooltipContent={disabled ? disabledTitle : null}
                    inputProps={{ step: "0.0001" }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show Orientation Axes (optional, only when handler provided) */}
      {typeof onToggleAxes === "function" && (
        <Checkbox
          label="Show Orientation Axes"
          checked={!!showAxes}
          onChange={onToggleAxes}
          disabled={disabled}
          tooltipContent={disabled ? disabledTitle : null}
          className="!pl-0"
        />
      )}
      {children}
    </div>
  );

  if (embedded) return editorContent;

  const collapsible = (
    <Collapsible open={open} onOpenChange={handleOpenChange}>
      {headerActions ? (
        <CollapsibleTrigger quiet actions={headerActions} aria-label={`Toggle ${title}`}>
          <span className="min-w-0 truncate text-left">{title}</span>
        </CollapsibleTrigger>
      ) : (
        <CollapsibleTrigger quiet>{title}</CollapsibleTrigger>
      )}
      <CollapsibleContent>{editorContent}</CollapsibleContent>
    </Collapsible>
  );

  return (
    <InspectorPhysicsLockedCollapsibleWrap locked={physicsAuthoringLocked}>
      {collapsible}
    </InspectorPhysicsLockedCollapsibleWrap>
  );
};

export default OffsetEditor;
